import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import createCrudService from '@core/api/create-crud-service';
import { request } from '@core/api/request';
import { QUERY_KEYS } from '@shared/constants/query-keys';
import type { TBaseQuery } from '@src/shared/types/base-query';
import type { TReminder } from '@src/shared/types/reminder';

export type TReminderQuery = {
  channel: TReminder['notification_channel'];
  type: TReminder['type'][];
  created_by: number | string;
  program: number | string;
  rule: number | string;
  stages?: (number | string)[];
  view: number | string;
  upcoming?: boolean;
} & TBaseQuery;

export type TReminderPayload = {
  name: string;
  content: string;
  type: string;
  notification_channel: string;
  members?: number[];
  attachments?: number[];
  leads?: number[];
  students?: number[];
  date?: string;
  timezone?: string;
  frequency?: number | string;
  frequency_count?: number;
  trigger_type?: string;
  variables?: Record<string, string>;
  is_rule_achieved?: boolean;
  rule_choices?: string[];
  rule?: number | string;
  stages?: number[];
  program?: number | string;
  question?: number | string;
  template?: number | string;
  periodic_task?: number | string;
  view?: number | string;
  subject?: string;
};

export type TReminderPerformanceMonth = {
  month: string;
  whatsapp: number;
  email: number;
  sms: number;
};

export type TReminderPerformanceResponse = {
  data: TReminderPerformanceMonth[];
  totals: { whatsapp: number; email: number; sms: number };
};

export type TReminderStatsResponse = {
  total_sent: number;
  scheduled: number;
  delivery_rate: number;
  failed: number;
};

export type TReminderSummaryStats = {
  sent: number;
  delivered: number;
  read: number;
  bounced: number;
  failed: number;
  total: number;
};

/** One channel row from `dashboard/reminders/channel-summary/`. */
export type TReminderSummaryChannelRow = {
  channel: string;
  stats: TReminderSummaryStats;
};

export type TReminderSummaryResponse = TReminderSummaryChannelRow[];

const reminderApi = {
  ...createCrudService<TReminder, TReminderQuery, TReminderPayload>({
    queryKey: [QUERY_KEYS.getReminders],
    url: 'reminders',
  }),

  useGetReminderStats: (
    queryParams?: Record<string, string | string[]>,
    options?: Partial<UseQueryOptions<TReminderStatsResponse>>
  ) =>
    useQuery({
      queryKey: [QUERY_KEYS.getReminderDashboardStats, queryParams],
      queryFn: async () =>
        await request<TReminderStatsResponse>({
          url: 'dashboard/reminders/stats/',
          method: 'GET',
          params: queryParams,
        }),
      staleTime: 60000,
      ...options,
    }),

  useGetReminderPerformance: (
    queryParams?: Record<string, string | string[]>,
    options?: Partial<UseQueryOptions<TReminderPerformanceResponse>>
  ) =>
    useQuery({
      queryKey: [QUERY_KEYS.getReminderDashboardPerformance, queryParams],
      queryFn: async () =>
        await request<TReminderPerformanceResponse>({
          url: 'dashboard/reminders/performance/',
          method: 'GET',
          params: queryParams,
        }),
      staleTime: 60000,
      ...options,
    }),

  useGetReminderSummary: (
    queryParams?: Record<string, string | string[]>,
    options?: Partial<UseQueryOptions<TReminderSummaryResponse>>
  ) =>
    useQuery({
      queryKey: [QUERY_KEYS.getReminderDashboardSummary, queryParams],
      queryFn: async () =>
        await request<TReminderSummaryResponse>({
          url: 'dashboard/reminders/channel-summary/',
          method: 'GET',
          params: queryParams,
        }),
      staleTime: 60000,
      ...options,
    }),
};

export default reminderApi;
